//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useEffect, useMemo, useState } from 'react';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  DndContext,
  DragEndEvent,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverEvent,
  DragOverlay,
  defaultDropAnimation
} from '@dnd-kit/core';
import styled from '@emotion/styled';
import { Button, ButtonGroup, IconButton, Tooltip, darken, lighten } from '@mui/material';

import { AllTableColumnsConfig, DataTableConfig } from '@/__configs/config_types';
import { TableColumn } from '@/redux/slices/slice_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { savePreferences } from '@/redux/slices/authSlice';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { deepCopy } from '@/_utils/utils';
import { useToast } from '@/components/_common/Toast';
import Modal from '@/components/_common/Modal/Modal';
import PolarisIcon from '@/components/_common/PolarisIcon';
import ResetButton from '@/components/_common/IconButtons/ResetButton';
import DroppableSection from '@/components/_common/Table/ColumnSelector/DroppableSection';
import DraggableColumnContent from '@/components/_common/Table/ColumnSelector/DraggableColumnContent';

type ColumnSelectorProps = {
  columns: TableColumn[];
  allColumns: AllTableColumnsConfig<unknown>;
};

export type ColumnDirectoryItem = {
  label: string;
  key: string;
  checked: boolean;
};

export type ColumnDirectory = {
  'in-use': ColumnDirectoryItem[];
  available: ColumnDirectoryItem[];
};

export type Sortable = {
  containerId: string;
  index: number;
  items: string[];
};

export type DragData = {
  sortable: Sortable;
  index: number;
} & ColumnDirectoryItem;

export type SectionAddress = { section: keyof ColumnDirectory; index: number };

const ColumnSelector = ({ columns, allColumns }: ColumnSelectorProps) => {
  const dispatch = useAppDispatch();
  const { error } = useToast();
  const [showResetBtn, setShowResetBtn] = React.useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const tableKey = useAppSelector(selectCurrentTableKey) as keyof DataTableConfig;
  const prefs = useAppSelector(selectUserPreferences);

  const buildColumnDirectory = (isDefault: boolean = false): ColumnDirectory => {
    const inUseLabels = columns.filter((col) => !col.hidden && col.label).map((col) => col.label);

    const directory: ColumnDirectory = {
      'in-use': [],
      available: []
    };

    Object.entries(allColumns).forEach(([key, col]) => {
      if (
        !col.label ||
        key === 'priority' ||
        key === 'expandable' ||
        key === 'overflow' ||
        col.position === 'left' ||
        col.position === 'right' ||
        col.omitted ||
        col.isCheckbox
      )
        return;

      if (isDefault) {
        directory[!col.defaultDisabled ? 'in-use' : 'available'].push({
          label: col.label,
          key,
          checked: false
        });
      } else {
        directory[inUseLabels.includes(col.label) ? 'in-use' : 'available'].push({
          label: col.label,
          key,
          checked: false
        });
      }
    });

    // this is to maintain the correct column order based on user's preference, which is the "columns" prop
    if (!isDefault) {
      directory['in-use'].sort((a, b) => columns.findIndex((c) => c.id === a.key) - columns.findIndex((c) => c.id === b.key));
    }

    return directory;
  };

  const [columnDirectory, setColumnDirectory] = useState<ColumnDirectory>(buildColumnDirectory());
  const inUseIsChecked = useMemo(() => columnDirectory['in-use'].some((item) => item.checked), [columnDirectory['in-use']]);
  const availableIsChecked = useMemo(() => columnDirectory['available'].some((item) => item.checked), [columnDirectory['available']]);
  const dataIsChecked = inUseIsChecked || availableIsChecked;
  const [activeDrag, setActiveDrag] = useState<DragData | null>(null);

  useEffect(() => {
    const defaultConfig = {
      visible: Object.keys(allColumns).filter((key) => !allColumns[key].defaultDisabled && !allColumns[key].omitted),
      hidden: Object.keys(allColumns).filter((key) => allColumns[key].defaultDisabled && !allColumns[key].omitted)
    };
    const currentConfig = {
      visible: columns.filter((col) => !col.hidden && !col.omitted).map((col) => col.id),
      hidden: columns.filter((col) => col.hidden && !col.omitted).map((col) => col.id)
    };

    setShowResetBtn(!(JSON.stringify(defaultConfig) === JSON.stringify(currentConfig)));
  }, [columns]);

  const resetColumns = () => {
    const newDirectory = buildColumnDirectory(true);
    setColumnDirectory(newDirectory);
  };

  const handleColumnsIconClick = () => {
    setIsOpen(true);
  };

  const handleOnSubmit = () => {
    const updatedColumns = [
      ...columnDirectory['in-use'].map((item, index) => ({
        id: item.key,
        order: index,
        hidden: false
      })),
      ...columnDirectory['available'].map((item) => ({
        id: item.key,
        order: null,
        hidden: true
      }))
    ];

    if (updatedColumns.length === 0) {
      error('At least 1 column is required.');
    } else {
      dispatch(savePreferences({ [tableKey]: { ...prefs[tableKey], columns: updatedColumns } }));
    }

    setIsOpen(false);
  };

  const handleOnCancel = () => {
    setIsOpen(false);
    // reset columns to what they were before after modal disappear animation
    setTimeout(() => {
      setColumnDirectory(buildColumnDirectory());
    }, 500);
  };

  const updateDirectoryListing = (origin: SectionAddress, destination: SectionAddress) => {
    const newDirectory = deepCopy(columnDirectory);

    const [updated] = newDirectory[origin.section].splice(origin.index, 1);
    newDirectory[destination.section].splice(destination.index, 0, updated);

    setColumnDirectory(newDirectory);
  };

  const onDragOver = (event: DragOverEvent) => {
    const { over } = event;

    const destinationSection: keyof ColumnDirectory = over?.data.current?.sortable?.containerId;

    if (destinationSection === 'in-use') {
      onDragEnd(event);
    }
  };

  const onDragEnd = (event: DragEndEvent | DragOverEvent) => {
    const { active, over } = event;

    if (!over) return;

    const destinationSection: keyof ColumnDirectory = over.data.current?.sortable?.containerId ?? over.id;

    updateDirectoryListing(
      {
        section: active.data.current?.sortable?.containerId,
        index: active.data.current?.index
      },
      {
        section: destinationSection,
        index: over.data.current?.index ?? 0
      }
    );
  };

  const handleCheckToggle = ({ section, index }: SectionAddress, newToggleValue: boolean) => {
    const newDirectory = deepCopy(columnDirectory);
    newDirectory[section][index].checked = newToggleValue;

    setColumnDirectory(newDirectory);
  };

  const handleBulkMovement = (enabledSection: boolean) => {
    const newDirectory = deepCopy(columnDirectory);
    const sectionToMoveTo = enabledSection ? 'in-use' : 'available';
    const otherSection = !enabledSection ? 'in-use' : 'available';

    const itemsToMove = newDirectory[otherSection].filter((item: ColumnDirectoryItem) => item.checked);
    newDirectory[otherSection] = [...newDirectory[otherSection].filter((item: ColumnDirectoryItem) => !item.checked)];
    newDirectory[sectionToMoveTo] = newDirectory[sectionToMoveTo].concat(
      itemsToMove.map((item: ColumnDirectoryItem) => {
        item.checked = false;
        return item;
      })
    );

    setColumnDirectory(newDirectory);
  };

  const alphabetizeDirectory = (data: ColumnDirectory['available']) => {
    return data.sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  return (
    <>
      <Tooltip title="Toggle Columns">
        <ColumnsIconButton
          data-testid="toggle-columns"
          color={showResetBtn ? 'primary' : 'default'}
          draggable={false}
          onClick={handleColumnsIconClick}
          size="large"
        >
          <PolarisIcon icon="columns" height={17} />
        </ColumnsIconButton>
      </Tooltip>
      <CustomizeColumnsModal
        icon={<PolarisIcon height={15} icon="columns" />}
        open={isOpen}
        title={'Customize Columns'}
        scrollDisabled
        onClose={handleOnCancel}
      >
        <ColumnSelectionContainer>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={(e) => {
              onDragEnd(e);
              setActiveDrag(null);
            }}
            onDragOver={onDragOver}
            onDragStart={(evt) => setActiveDrag(evt.active.data.current as DragData)}
          >
            <DroppableSection
              title="Available"
              data={alphabetizeDirectory(columnDirectory['available'])}
              sectionKey="available"
              checkCallback={handleCheckToggle}
            />
            <BulkActionArrows>
              <Arrows orientation="vertical" variant="outlined">
                <ArrowButton onClick={() => handleBulkMovement(false)} disabled={!dataIsChecked || (!inUseIsChecked && availableIsChecked)}>
                  <ArrowIcon className="left" icon="play" />
                </ArrowButton>
                <ArrowButton onClick={() => handleBulkMovement(true)} disabled={!dataIsChecked || (inUseIsChecked && !availableIsChecked)}>
                  <ArrowIcon className="right" icon="play" />
                </ArrowButton>
              </Arrows>
            </BulkActionArrows>
            <DroppableSection title="In Use" data={columnDirectory['in-use']} sectionKey="in-use" checkCallback={handleCheckToggle} />
            <DragOverlay dropAnimation={defaultDropAnimation}>
              {activeDrag ? (
                <DraggableColumnContent
                  id={activeDrag.key}
                  index={activeDrag.index}
                  label={activeDrag.label}
                  checked={false}
                  sectionKey={activeDrag.sortable.containerId}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </ColumnSelectionContainer>
        <ButtonRow>
          <ResetButton onClick={resetColumns} />
          <div>
            <Button color="default" variant="text" onClick={handleOnCancel} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button color="primary" variant="contained" onClick={handleOnSubmit} disabled={columnDirectory['in-use'].length < 1}>
              Save
            </Button>
          </div>
        </ButtonRow>
      </CustomizeColumnsModal>
    </>
  );
};

export default ColumnSelector;

const ColumnsIconButton = styled(IconButton)`
  top: 0;
  padding: ${({ theme }) => theme.spacing(0.75)};
  user-select: none;
`;

const ColumnSelectionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 55px 1fr;
`;

const Arrows = styled(ButtonGroup)`
  .MuiButtonGroup-grouped {
    padding: 6px;
    min-width: 24px;
  }
`;

const ArrowButton = styled(Button)`
  border-color: transparent;
  background: ${({ theme }) =>
    theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.1) : lighten(theme.palette.background.paper, 0.2)};

  &:hover {
    border-color: transparent;
    background: ${({ theme }) =>
      theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.15) : lighten(theme.palette.background.paper, 0.25)};
  }

  &.MuiButtonGroup-grouped:not(:last-of-type):hover {
    border-color: transparent;
  }
  &:disabled {
    opacity: 0.3;
    color: ${({ theme }) => theme.palette.text.disabled};
  }
`;

const ArrowIcon = styled(PolarisIcon)`
  height: ${({ theme }) => theme.spacing(2)};
  width: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => lighten(theme.palette.text.primary, 0.5)};

  &.left {
    transform: rotate(180deg);
  }
`;

const BulkActionArrows = styled.div`
  position: relative;
  width: 55px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const CustomizeColumnsModal = styled(Modal)`
  display: flex;
  flex-direction: column;
`;

const ButtonRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacing(2)};
`;
